import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { BloqueDeRubro, COLUMNAS_DEL_CUADRO } from "@/components/generacion/BloqueDeRubro";
import { TipoDeCambioDelInforme } from "@/components/generacion/TipoDeCambioDelInforme";
import { TotalesDeLaValorizacion } from "@/components/generacion/TotalesDeLaValorizacion";
import { useHechos } from "@/hooks/useGeneracion";
import { useValorizacion } from "@/hooks/useValorizacion";
import { getApiErrorMessage } from "@/lib/api";
import { cn } from "@/lib/utils";

interface ValorizacionDelInformeProps {
  informeId: number;
}

/**
 * La fase 2: el valor del predio por rubro y por línea, los castigos de
 * liquidación y los totales en UF, pesos y dólares.
 *
 * Las cifras las calcula el servidor; esta pantalla propone, deja corregir con
 * un motivo y muestra lo que falta. Nada se guarda sin un acto de la persona.
 */
export function ValorizacionDelInforme({ informeId }: ValorizacionDelInformeProps) {
  const valorizacion = useValorizacion(informeId);
  // La fuente de la UF y del dólar vive en la hoja de hechos, no en la valorización.
  const hechos = useHechos(informeId);

  if (valorizacion.isPending) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-28 w-full rounded-xl" />
        <Skeleton className="h-64 w-full rounded-xl" />
      </div>
    );
  }

  if (valorizacion.isError) {
    return (
      <Alert variant="destructive">
        <AlertTitle>No pudimos calcular la valorización</AlertTitle>
        <AlertDescription>
          <p>{getApiErrorMessage(valorizacion.error, "Inténtalo de nuevo en unos segundos.")}</p>
          <Button type="button" size="sm" variant="outline" className="mt-2" onClick={() => void valorizacion.refetch()}>
            Reintentar
          </Button>
        </AlertDescription>
      </Alert>
    );
  }

  const datos = valorizacion.data;

  return (
    <div className="space-y-4">
      <div className="grid items-start gap-3.5 xl:grid-cols-[minmax(0,1fr)_18.75rem]">
        <section className="bg-card min-w-0 overflow-hidden rounded-[1.25rem] border-[1.5px]">
          <header className="flex flex-wrap items-baseline gap-x-3 gap-y-1 px-5 pt-4 pb-3.5">
            <h3 className="text-ink text-[0.9375rem] font-extrabold tracking-[-0.01em]">Propuesta de valor por rubro</h3>
            <p className="text-ink-muted text-xs font-semibold">
              Las cifras las calcula el sistema. Acepta la propuesta, corrígela con un motivo o completa el precio de cada
              línea.
            </p>
          </header>
          <div
            className={cn(
              "bg-superficie-tenue border-border rotulo-seccion grid gap-x-3 border-y-[1.5px] px-5 py-2.5",
              COLUMNAS_DEL_CUADRO,
            )}
          >
            <span>Rubro</span>
            <span>Propuesta · origen</span>
            <span>Valor final (UF)</span>
            <span>Estado</span>
            <span aria-hidden />
          </div>
          {datos.rubros.map((rubro) => (
            <BloqueDeRubro
              key={rubro.rubro}
              informeId={informeId}
              rubro={rubro}
              castigoGlobal={datos.liquidacion.castigo_global_pct}
            />
          ))}
        </section>

        <TotalesDeLaValorizacion informeId={informeId} valorizacion={datos} />
      </div>

      <TipoDeCambioDelInforme informeId={informeId} tipoCambio={datos.tipo_cambio} hechos={hechos.data ?? []} />
    </div>
  );
}
